import type { TFunction } from 'next-i18next';

import type { McStrings, MiStrings, WinsStrings } from './engine/strings';

// Builds the runtime copy of the live demos from the LP's namespace. Everything the engines insert with
// innerHTML goes through `html`, which turns i18next's own escaping off: the templates carry the markup
// (<b>, the live number spans) and the engines escape every value that comes from data before it gets
// here. Plain-text sentences keep i18next's defaults.

type Vars = Record<string, unknown>;

export function makeTranslators(t: TFunction) {
  const text = (key: string, vars?: Vars) => String(t(key, { ...vars, interpolation: { escapeValue: false } }));
  return { text, html: text };
}

// The site's locale slugs are not all BCP 47 tags (cn, jp, kr, sa): map them before Intl sees them.
const INTL_LOCALES: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es', fr: 'fr', ru: 'ru', tr: 'tr', cn: 'zh-CN', jp: 'ja', kr: 'ko', sa: 'ar' };
export const intlLocale = (locale: string) => INTL_LOCALES[locale] ?? locale;
/** Latin digits everywhere, like the rest of the page (ar would default to Arabic-Indic ones). */
const numberLocale = (locale: string) => `${intlLocale(locale)}-u-nu-latn`;

export function numberFormatter(locale: string, fractionDigits = 0) {
  const format = new Intl.NumberFormat(numberLocale(locale), { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
  return (value: number) => format.format(value);
}

export function listFormatter(locale: string) {
  let format: Intl.ListFormat | null = null;
  try {
    format = new Intl.ListFormat(intlLocale(locale), { style: 'long', type: 'conjunction' });
  } catch {
    format = null;
  }
  return (items: string[]) => (format ? format.format(items) : items.join(', '));
}

/** Country names in the visitor's language; the geo table's (pt) name when the runtime has none. */
export function countryNamer(locale: string) {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([intlLocale(locale)], { type: 'region' });
  } catch {
    names = null;
  }
  return (code: string, fallback: string) => {
    if (locale.startsWith('pt') || !names) return fallback;
    try {
      return names.of(code) || fallback;
    } catch {
      return fallback;
    }
  };
}

export function buildMcStrings(t: TFunction, locale: string): McStrings {
  const { text, html } = makeTranslators(t);
  const list = listFormatter(locale);
  const k = (key: string) => `mc.${key}`;
  return {
    yourInternet: text(k('your_internet')),
    matchIn: (city) => text(k('match_in'), { city }),
    description: (routes, hub) => text(k('description'), { routes: list(routes), hub }),
    activeNow: text(k('active_now')),
    slowerBy: (ms) => text(k('slower_by'), { ms }),
    proofSr: (bad, lat, pct) => text(k('proof_sr'), { bad, lat, pct }),
    statusChanged: (prev, prevMs, best, bestMs) => html(k('status_changed'), { prev, prevMs, best, bestMs }),
    statusMeasured: (best, bestMs) => html(k('status_measured'), { best, bestMs }),
    statusLead: (best, bestMs) => html(k('status_lead'), { best, bestMs }),
    statusPaused: (best, bestMs) => html(k('status_paused'), { best, bestMs }),
    focusWinner: (city, ms) => html(k('focus_winner'), { city, ms }),
    focusNext: (city, delta, best) => html(k('focus_next'), { city, delta, best }),
    focusReserve: (city, delta, best) => html(k('focus_reserve'), { city, delta, best }),
    routeAriaWinner: (city, country, lat) => text(k('route_aria_winner'), { city, country, lat }),
    routeAriaSlower: (city, country, lat, delta) => text(k('route_aria_slower'), { city, country, lat, delta }),
    routeAriaMeasuring: (city, country) => text(k('route_aria_measuring'), { city, country }),
    directAria: (delta) => text(k('direct_aria'), { delta }),
    directAriaMeasuring: text(k('direct_aria_measuring')),
    retestLabel: text(k('retest')),
    measuringLabel: text(k('measuring')),
    statusMeasuring: text(k('status_measuring')),
    liveMeasuring: text(k('live_measuring')),
    liveMeasured: (best, lat, pct, prev) => text(k('live_measured'), { best, lat, pct, handover: prev ? text(k('live_handover'), { prev, best }) : '' }),
    liveCleared: text(k('live_cleared')),
    livePinnedWinner: (city, lat) => text(k('live_pinned_winner'), { city, lat }),
    livePinnedSlower: (city, lat, delta, winner) => text(k('live_pinned_slower'), { city, lat, delta, winner }),
    livePaused: text(k('live_paused')),
    liveResumed: text(k('live_resumed')),
  };
}

const RESUMED_KEYS = ['resumed_none', 'resumed_single', 'resumed_several'];

const capitalize = (value: string) => value.charAt(0).toLocaleUpperCase() + value.slice(1);

export function buildMiStrings(t: TFunction, locale: string): MiStrings {
  const { text, html } = makeTranslators(t);
  const list = listFormatter(locale);
  const decimalFormat = new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 2 });
  const k = (key: string) => `mi.${key}`;
  const pair = (key: string, vars?: Vars): [string, string] => [html(k(`${key}_long`), vars), html(k(`${key}_short`), vars)];
  return {
    mbps: text(k('mbps')),
    gbps: text(k('gbps')),
    decimal: (value) => decimalFormat.format(value),
    srMbps: (value) => text(k('sr_mbps'), { value }),
    srGbps: (value) => text(k('sr_gbps'), { value }),
    noBandwidth: text(k('no_bandwidth')),
    kind: { fiber: text(k('kind.fiber')), cable: text(k('kind.cable')), dsl: text(k('kind.dsl')), wireless: text(k('kind.wireless')), satellite: text(k('kind.satellite')) },
    kind5g: text(k('kind.g5')),
    kind4g: text(k('kind.g4')),
    kindWifi: text(k('kind.wifi')),
    yoursSuffix: text(k('yours_suffix')),
    yoursSr: text(k('yours_sr')),
    backupRest: [text(k('backup_rest')), text(k('backup_rest_sub'))],
    backupHot: [text(k('backup_hot')), text(k('backup_hot_sub'))],
    backupLone: [text(k('backup_lone')), text(k('backup_lone_sub'))],
    backupNone: [text(k('backup_none')), text(k('backup_none_sub'))],
    connAria: (name, mbps) => text(k('conn_aria'), { name, mbps }),
    connAriaDownCovered: text(k('conn_aria_down_covered')),
    connAriaDownAlone: text(k('conn_aria_down_alone')),
    connAriaOn: text(k('conn_aria_on')),
    connAriaOff: text(k('conn_aria_off')),
    activeWord: (count) => text(k('active_word'), { count }),
    tagDown: text(k('tag_down')),
    tagOn: text(k('tag_on')),
    tagOff: text(k('tag_off')),
    deltaDown: text(k('delta_down')),
    deltaOn: text(k('delta_on')),
    deltaOff: text(k('delta_off')),
    drops: (count) => text(k('drops'), { count }),
    proofSr: (base, sum, pct, drops) => text(k('proof_sr'), { base, sum, drops, extra: pct > 0 ? text(k('proof_sr_extra'), { pct }) : '' }),
    srNowOut: text(k('sr_now_out')),
    srNowNone: text(k('sr_now_none')),
    srNowActive: (count, total, sum, combined, drops, tail) => text(k('sr_now_active'), { combo: text(k('combo'), { count }), total, sum, drops, combined: combined ? text(k('sr_now_combined')) : '', tail: text(k(`sr_now_tail_${tail}`)) }),
    npSubNoReserve: text(k('np_sub_no_reserve')),
    npSubNoConnection: text(k('np_sub_no_connection')),
    npSubLone: text(k('np_sub_lone')),
    npSubTookOver: text(k('np_sub_took_over')),
    npSubCombines: text(k('np_sub_combines')),
    gameSubDropped: text(k('game_sub_dropped')),
    gameSubWaiting: text(k('game_sub_waiting')),
    gameSubNoReserve: text(k('game_sub_no_reserve')),
    gameSubContinuous: text(k('game_sub_continuous')),
    sumOut: text(k('sum_out')),
    sumNone: text(k('sum_none')),
    sumOneDrop: text(k('sum_one_drop')),
    sumNoReserve: text(k('sum_no_reserve')),
    sumContinuous: text(k('sum_continuous')),
    sumSubNone: html(k('sum_sub_none')),
    sumSubLoneKicked: (name) => pair('sum_sub_lone_kicked', { name }),
    sumSubLone: (name) => pair('sum_sub_lone', { name }),
    sumSubPair: (names) => pair('sum_sub_pair', { names }),
    sumSubMany: (count) => pair('sum_sub_many', { count }),
    dropTitleWait: text(k('drop_title_wait')),
    dropTitleReconnect: text(k('drop_title_reconnect')),
    dropTitleDefault: text(k('drop_title_default')),
    dropTitleAlone: text(k('drop_title_alone')),
    stabNone: text(k('stab_none')),
    stabLone: text(k('stab_lone')),
    stabOk: text(k('stab_ok')),
    noteFailed: (name) => html(k('note_failed'), { name }),
    noteNoReserve: pair('note_no_reserve'),
    noteOneTook: (name) => pair('note_one_took', { name }),
    noteManyTook: (count) => html(k('note_many_took'), { count }),
    notePairHold: (first, second) => pair('note_pair_hold', { first, second }),
    sayFailedAlone: (name) => html(k('say_failed_alone'), { name }),
    sayFailedCovered: (name) => html(k('say_failed_covered'), { name }),
    srFailedAlone: (name) => text(k('sr_failed_alone'), { name }),
    srFailedCovered: (name, via) => text(k('sr_failed_covered'), { name, via }),
    srViaMany: (count) => text(k('sr_via_rest'), { count }),
    srVia: (name) => text(k('sr_via'), { name }),
    srRecoveredMany: (name, combo) => text(k('sr_recovered_several'), { name, combo: capitalize(combo) }),
    srRecoveredKicked: (name) => text(k('sr_recovered_kicked'), { name }),
    srRecoveredLone: (name) => text(k('sr_recovered_lone'), { name }),
    noteBackLone: (name) => pair('note_back_lone', { name }),
    noteBackMany: (name, count) => pair('note_back_many', { name, count }),
    sayBackLone: (name) => html(k('say_back_lone'), { name }),
    sayBackMany: (name) => html(k('say_back_several'), { name }),
    sayResetExtras: (names, count) => html(k('say_reset_extras'), { names, count }),
    sayReset: html(k('say_reset')),
    sayAllActive: html(k('say_all_active')),
    sayAdded: (name, second) => html(k(second ? 'say_added_second' : 'say_added'), { name }),
    loneKicked: (name) => html(k('lone_kicked'), { name }),
    lone: (name) => html(k('lone'), { name }),
    sayKeepOne: html(k('say_keep_single')),
    liveKeepOne: text(k('live_keep_single')),
    sayToReserve: (name) => html(k('say_to_reserve'), { name }),
    liveToReserve: (name, rest) => text(k('live_to_reserve'), { name, rest }),
    liveOnlyOne: (name) => text(k('live_only_single'), { name }),
    sayJoined: (name) => html(k('say_joined'), { name }),
    liveJoined: (name, rest) => text(k('live_joined'), { name, rest }),
    liveOnlyOneNoReserve: text(k('live_single_no_reserve')),
    combo: (count) => text(k('combo'), { count }),
    sayActivateToDrop: html(k('say_activate_to_drop')),
    dropLabel: text(k('drop')),
    reconnectingLabel: text(k('reconnecting')),
    reconnectLabel: text(k('reconnect')),
    pauseAria: text(k('pause_aria')),
    resumeAria: text(k('resume_aria')),
    pausedStateDown: (holding) => html(k('paused_state_down'), { holding }),
    pausedHoldingOne: (name) => html(k('paused_holding_single'), { name }),
    pausedHoldingMany: (count) => html(k('paused_holding_rest'), { count }),
    pausedNoReserve: html(k('paused_no_reserve')),
    pausedCombined: (combo) => html(k('paused_combined'), { combo }),
    pausedOnlyOne: (name) => html(k('paused_only_single'), { name }),
    pausedNone: html(k('paused_none')),
    sayPaused: (state) => html(k('say_paused'), { state }),
    sayResumed: (state, count) => capitalize(state) + text(k(RESUMED_KEYS[Math.min(count, 2)] ?? 'resumed_none')),
    livePaused: text(k('live_paused')),
    liveResumed: text(k('live_resumed')),
    list,
    description: (connections) => text(k('description'), { connections }),
  };
}

export function buildWinsStrings(t: TFunction): WinsStrings {
  return { ispOk: String(t('wins.stability.ok')), ispDown: String(t('wins.stability.down')) };
}
